import { useContext, useState } from 'react'

import {
  convertAiTagConditionToVars,
  useAiTagGenerationsApi,
  useAiTagListApi,
  useAiTagSearchCondition,
  useDeleteAiTagMutation,
  useDeleteAllAiTagsMutation
} from '@/common/api/modules/aiTag'
import { useFirstLoading } from '@/common/api/search'
import { useAiTagAnalysis } from '@/common/app/modules/recommend/aiTag'
import { aiTagNewPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { dateTimeStr } from '@/common/form/date'
import { AITagMaster } from '@/common/model/recommend/aiTag'
import { useSelectIds } from '@/common/util/select'

import AiButton from '@/components/view/atoms/button/AiButton'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import DisclosureRow from '@/components/view/atoms/disclosure/DisclosureRow'
import Icon, { IconType } from '@/components/view/atoms/icon/Icon'
import Loading from '@/components/view/atoms/icon/Loading'
import Tab from '@/components/view/atoms/tab/Tab'
import TabList from '@/components/view/atoms/tab/TabList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageListLoading from '@/components/view/molecules/loading/PageListLoading'
import PageListSearching from '@/components/view/molecules/loading/PageListSearching'
import NoResultGuidance from '@/components/view/molecules/search/NoResultGuidance'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import AppTitleBadge from '@/components/container/layout/AppTitleBadge'
import AITagGenerationModalProvider, {
  AITagGenerationModalContext
} from '@/components/container/modal/aiTag/AITagGenerationModalProvider'
import AITagPreviewModal from '@/components/container/modal/aiTag/AITagPreviewModal'
import { ConfirmDialogContext } from '@/components/container/modal/dialog/confirm/ConfirmDialogProvider'
import OperationButton from '@/components/container/modules/master/operation/OperationButton'
import AITagEffectivenessTab from '@/components/container/modules/recommend/aiTag/AITagEffectivenessTab'
import AITagProgressBar from '@/components/container/modules/recommend/aiTag/AITagProgressBar'
import AITagListTable from '@/components/container/modules/recommend/aiTag/table/AITagListTable'
import AITagSearchConditionField from '@/components/container/modules/recommend/aiTag/table/AITagSearchConditionField'
import TitlePageInfo from '@/components/container/utils/pager/TitlePageInfo'

const VIEW_MODE_TABS = [
  { key: 'tags', label: 'タグ一覧' },
  { key: 'effectiveness', label: 'タグ効果ランキング' }
] as const

type ViewMode = (typeof VIEW_MODE_TABS)[number]['key']

const AITagListPageInner = () => {
  const siteId = useSiteId()

  const [viewMode, setViewMode] = useState<ViewMode>('tags')
  const [previewTag, setPreviewTag] = useState<AITagMaster | null>(null)

  const [condition, setCondition, onUpdatePage] = useAiTagSearchCondition()

  const { data, isLoading, mutate } = useAiTagListApi(
    convertAiTagConditionToVars(siteId, condition)
  )

  const { selectedIds, setSelectedIds, onSelectId, onSelectAll } = useSelectIds(
    data?.tags ?? [],
    condition
  )
  const isFirstLoading = useFirstLoading(isLoading)

  const { data: generationsData, mutate: mutateGenerations } = useAiTagGenerationsApi({
    site_id: siteId
  })

  const { trigger: deleteAllTags } = useDeleteAllAiTagsMutation()
  const { trigger: deleteTag } = useDeleteAiTagMutation()

  const { analyzing, diffAnalyzing, isProcessing, progress, analyzeMsg, analyze, diffAnalyze } =
    useAiTagAnalysis({
      siteId,
      onComplete: () => {
        mutate()
        mutateGenerations()
      }
    })

  const tags = data?.tags ?? []
  const total = data?.total ?? 0

  const { showModal: showConfirmDialog } = useContext(ConfirmDialogContext)
  const { showModal: showGenerationModal } = useContext(AITagGenerationModalContext)

  const handleDeleteAll = async () => {
    showConfirmDialog({
      message: `全てのAIタグを削除します。この操作は元に戻せません。よろしいですか？`,
      yesText: '削除する',
      noText: 'キャンセル',
      onClickYes: async () => {
        await deleteAllTags({ site_id: siteId })
        mutate()
      }
    })
  }

  return (
    <>
      <Main type={MainLayoutType.FULL}>
        <PageTitle
          title='AIタグ分析'
          badgeNode={<AppTitleBadge type='recommend' />}
          description='商品・コンテンツデータからAIが重要タグを抽出し、レコメンドに活用します'
          behindNode={
            data && (
              <TitlePageInfo
                page={condition.page}
                perPage={condition.perPage}
                total={data.total}
                unit='件'
              />
            )
          }
          rightNode={
            <>
              {generationsData?.current?.analyzed_at && (
                <Text size={12} color={TextColor.SECONDARY}>
                  最終解析:
                  {dateTimeStr(generationsData.current.analyzed_at)}
                </Text>
              )}
              <Button
                square
                size='large'
                color={ButtonColor.SECONDARY}
                onClick={() => showGenerationModal({ siteId, onRestored: mutate })}
              >
                <Icon type={IconType.HISTORY} />
              </Button>
              <AiButton
                onClick={analyze}
                loading={analyzing}
                disabled={isProcessing}
                size={'large'}
              >
                AIタグ分析を実行
              </AiButton>
              <Button
                onClick={diffAnalyze}
                disabled={isProcessing || analyzing}
                loading={diffAnalyzing}
                size={'large'}
              >
                追加データの分析
              </Button>
              <Button link={aiTagNewPath(siteId)} size={'large'}>
                タグを追加する
              </Button>
              <OperationButton
                node={
                  <>
                    <DisclosureRow
                      onClick={handleDeleteAll}
                      disabled={data ? data.total === 0 : true}
                    >
                      <Text
                        lineHeight={150}
                        color={
                          data
                            ? data.total === 0
                              ? TextColor.DISABLED
                              : TextColor.PRIMARY
                            : TextColor.DISABLED
                        }
                      >
                        全削除する
                      </Text>
                    </DisclosureRow>
                  </>
                }
              />
            </>
          }
        />
        <Content>
          <AITagProgressBar analyzing={analyzing} progress={progress} analyzeMsg={analyzeMsg} />
          <Area>
            <TabList>
              {VIEW_MODE_TABS.map((tab) => (
                <Tab
                  key={tab.key}
                  onClick={() => setViewMode(tab.key)}
                  selected={viewMode == tab.key}
                >
                  {tab.label}
                </Tab>
              ))}
            </TabList>
          </Area>
          {viewMode === 'effectiveness' && <AITagEffectivenessTab siteId={siteId} />}
          {viewMode === 'tags' && (
            <>
              {isFirstLoading ? (
                <PageListLoading />
              ) : (
                <Area tableWrap noBorder>
                  <AITagSearchConditionField condition={condition} setCondition={setCondition} />
                  {isLoading ? (
                    <PageListSearching />
                  ) : data?.total ? (
                    <>
                      <AITagListTable
                        tags={tags}
                        total={total}
                        condition={condition}
                        setCondition={setCondition}
                        onUpdatePage={onUpdatePage}
                        selectedIds={selectedIds}
                        onSelect={onSelectId}
                        onSelectAll={onSelectAll}
                        reload={() => {
                          mutate()
                          setSelectedIds([])
                        }}
                        onPreview={setPreviewTag}
                      />
                    </>
                  ) : (
                    <NoResultGuidance searched name='AIタグ' />
                  )}
                </Area>
              )}
            </>
          )}
        </Content>
      </Main>
      <AITagPreviewModal tag={previewTag} siteId={siteId} onClose={() => setPreviewTag(null)} />
    </>
  )
}

const AITagListPage = () => (
  <AITagGenerationModalProvider>
    <AITagListPageInner />
  </AITagGenerationModalProvider>
)

export default AITagListPage
